import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getPlatformAPI } from '../lib/platform'
import type { Mode, Theme } from '../lib/platform/types'

interface ModeProviderContextProps {
  mode: Mode
  resolvedMode: 'dark' | 'light'
  setMode: (mode: Mode) => void
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ModeContext = createContext<ModeProviderContextProps | undefined>(undefined)

interface ModeProviderProps {
  children: React.ReactNode
  defaultMode?: Mode
}

/**
 * ModeProvider Component
 *
 * Manages application mode state (light/dark/system) and applies mode classes to DOM.
 * Supports 'light', 'dark', and 'system' modes.
 * Persists mode via platform adapters (Electron: Dexie + localStorage, Web: Supabase + localStorage).
 * Syncs mode across Electron windows via IPC.
 *
 * @param {React.ReactNode} children - Child components
 * @param {Mode} [defaultMode] - Optional default mode (fallback if nothing saved)
 */
export function ModeProvider({ children, defaultMode }: ModeProviderProps) {
  const platformAPI = getPlatformAPI()

  const [mode, setMode] = useState<Mode>(() => {
    // Read synchronously from localStorage for instant initialization (no flash)
    const stored = localStorage.getItem('mode') as Mode | null
    return stored || defaultMode || 'light'
  })

  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('theme') as Theme | null
    return stored || 'standard'
  })

  const [resolvedMode, setResolvedMode] = useState<'dark' | 'light'>(() => {
    const stored = localStorage.getItem('mode') as Mode | null
    const initial = stored || defaultMode || 'light'
    if (initial === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return initial === 'dark' ? 'dark' : 'light'
  })

  useEffect(() => {
    const root = document.documentElement

    const applyMode = (isDark: boolean) => {
      if (isDark) {
        root.classList.add('dark')
        root.classList.remove('light')
        setResolvedMode('dark')
      } else {
        root.classList.add('light')
        root.classList.remove('dark')
        setResolvedMode('light')
      }

      // Sync body classes (needed for CSS selectors like `.dark .switch`)
      if (isDark) {
        document.body.classList.add('dark')
        document.body.classList.remove('light')
      } else {
        document.body.classList.add('light')
        document.body.classList.remove('dark')
      }

      // Trigger inline theme override scripts (anti-flash layer)
      if (typeof window.updateMainWindowTheme === 'function') {
        window.updateMainWindowTheme()
      }
      if (typeof window.updatePreferencesTheme === 'function') {
        window.updatePreferencesTheme()
      }
    }

    if (mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handleChange = (e: MediaQueryListEvent) => {
        applyMode(e.matches)
      }

      // Apply initial system mode
      applyMode(mediaQuery.matches)

      // Listen for system mode changes
      mediaQuery.addEventListener('change', handleChange)

      return () => {
        mediaQuery.removeEventListener('change', handleChange)
      }
    } else {
      applyMode(mode === 'dark')
      return undefined
    }
  }, [mode])

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'standard') {
      root.removeAttribute('data-theme')
    } else {
      root.setAttribute('data-theme', theme)
    }

    // Re-run the inline anti-flash overrides so the main / preferences window's
    // theme-override <style> picks up the new theme (each function reads
    // localStorage('theme') itself).
    if (typeof window.updateMainWindowTheme === 'function') {
      window.updateMainWindowTheme()
    }
    if (typeof window.updatePreferencesTheme === 'function') {
      window.updatePreferencesTheme()
    }
  }, [theme])

  const setModeAndStore = useCallback(async (newMode: Mode) => {
    try {
      await platformAPI.setMode(newMode)
      setMode(newMode)
    } catch (error) {
      console.error('[ModeProvider] Failed to set mode:', error)
    }
  }, [platformAPI])

  const setThemeAndStore = useCallback(async (newTheme: Theme) => {
    try {
      await platformAPI.setTheme(newTheme)
      setTheme(newTheme)
    } catch (error) {
      console.error('[ModeProvider] Failed to set theme:', error)
    }
  }, [platformAPI])

  // Listen for mode changes from other windows (Electron only)
  useEffect(() => {
    if (platformAPI.platform === 'electron' && platformAPI.onModeChanged) {
      const cleanup = platformAPI.onModeChanged((newMode: Mode) => {
        if (mode !== newMode) {
          setMode(newMode)
        }
      })

      return cleanup
    }
    return undefined
  }, [mode, platformAPI])

  // Initialize mode from platform on mount
  useEffect(() => {
    const initializeMode = async () => {
      try {
        const savedMode = await platformAPI.getMode()
        if (savedMode && savedMode !== mode) {
          setMode(savedMode)
        }
      } catch (error) {
        console.error('[ModeProvider] Failed to load mode:', error)
      }
    }

    initializeMode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initialize theme from shared platform storage on mount — separate from
  // localStorage which is per-BrowserWindow in Electron and therefore stale
  // when the user toggled theme in another window.
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        const savedTheme = await platformAPI.getTheme()
        if (savedTheme && savedTheme !== theme) {
          localStorage.setItem('theme', savedTheme)
          setTheme(savedTheme)
        }
      } catch (error) {
        console.error('[ModeProvider] Failed to load theme:', error)
      }
    }

    initializeTheme()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for theme changes from other windows
  useEffect(() => {
    const cleanup = platformAPI.onThemeChanged((newTheme: Theme) => {
      setTheme(prev => prev !== newTheme ? newTheme : prev)
    })
    return cleanup
  }, [platformAPI])

  // Listen for menu mode toggle events
  useEffect(() => {
    const handleMenuThemeToggle = () => {
      // Toggle between dark and light (not system)
      const newMode = resolvedMode === 'dark' ? 'light' : 'dark'
      setModeAndStore(newMode)
    }

    window.addEventListener('menu-theme-toggle', handleMenuThemeToggle)

    return () => {
      window.removeEventListener('menu-theme-toggle', handleMenuThemeToggle)
    }
  }, [resolvedMode])

  const value = useMemo(() => ({
    mode,
    resolvedMode,
    setMode: setModeAndStore,
    theme,
    setTheme: setThemeAndStore,
  }), [mode, resolvedMode, setModeAndStore, theme, setThemeAndStore])

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  )
}

export function useMode() {
  const context = useContext(ModeContext)
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider')
  }
  return context
}

// Re-export Mode and Theme types for convenience
export type { Mode, Theme } from '../lib/platform/types'
